import { Routes } from '@angular/router';
import { Component } from '@angular/core';
import { deliveryEnabledGuard } from './features/table-connect/delivery/delivery-enabled.guard';
import { customerAuthGuard } from './features/table-connect/account/account.guard';

@Component({
  selector: 'app-not-found',
  standalone: true,
  template: `
    <main style="min-height: 100dvh; display: grid; place-items: center; padding: 24px; text-align: center;">
      <div>
        <div style="font-size: 48px; line-height: 1;" aria-hidden="true">⌫</div>
        <h1 style="font-size: 18px; font-weight: 700; margin: 12px 0 4px;">This link is not available.</h1>
        <p style="font-size: 14px; color: #6b7280; margin: 0;">Please ask the restaurant team for assistance.</p>
      </div>
    </main>
  `,
})
export class NotFoundComponent {}

export const routes: Routes = [
  // TSS-3 — tenant-wide store selector. The ONLY route not under /t/:publicToken:
  // it is scoped to a tenant, not a store. Selecting a store redirects into the
  // existing /t/... flow, which is untouched by this feature.
  //
  // No `data.header` — the global TcAppHeader keys off a store publicToken
  // (AppComponent.showHeader) and is meaningless here, so it stays hidden and
  // the screen owns its own chrome.
  {
    path: 's/:tenantToken',
    loadComponent: () => import('./features/tenant-selector/store-selector.component').then(m => m.StoreSelectorComponent),
  },
  // TC-2 — public, no-auth TableConnect guest landing. Reachable from a scanned table QR.
  {
    path: 't/:publicToken',
    loadComponent: () => import('./features/table-connect/guest-landing/guest-landing.component').then(m => m.GuestLandingComponent),
    // Landing keeps its hero brand block; the global header sits above (chip only, no back).
    data: { header: { show: true, showBack: false } },
  },
  // TC-3 — public, no-auth TableConnect menu.
  {
    path: 't/:publicToken/menu',
    loadComponent: () => import('./features/table-connect/menu/menu.component').then(m => m.TableConnectMenuComponent),
    // Title/subtitle (store · room · table) set dynamically via HeaderConfigService.
    data: { header: { show: true, backAriaLabel: 'Back to landing', backHref: [] } },
  },
  // CC-TC-3 — department (cuisine) wall for multi-cuisine venues. Reached from
  // the landing when the store's DepartmentListingEnabled flag is on; the
  // component itself degrades to the flat menu when the flag is off/missing,
  // so bookmarked URLs are never a dead end.
  {
    path: 't/:publicToken/departments',
    loadComponent: () => import('./features/table-connect/department-wall/department-wall.component').then(m => m.DepartmentWallComponent),
    data: { header: { show: true, backAriaLabel: 'Back to landing', backHref: [] } },
  },
  // CC-TC-3 — department-scoped menu. Reuses the flat menu component; the
  // :departmentId param filters categories/items/search to one cuisine while
  // the cart stays shared across cuisines (one order, many kitchens).
  {
    path: 't/:publicToken/departments/:departmentId/menu',
    loadComponent: () => import('./features/table-connect/menu/menu.component').then(m => m.TableConnectMenuComponent),
    data: { header: { show: true, backAriaLabel: 'Back to cuisines', backHref: ['departments'] } },
  },
  // TC-4 — public, no-auth cart review (items + checkout CTA).
  {
    path: 't/:publicToken/cart',
    loadComponent: () => import('./features/table-connect/cart/cart.component').then(m => m.TableConnectCartComponent),
    data: { header: { show: true, title: 'Your Cart', backAriaLabel: 'Back to menu', backHref: ['menu'] } },
  },
  // TC-4B — public, no-auth checkout (contact form + submit + payment).
  {
    path: 't/:publicToken/checkout',
    loadComponent: () => import('./features/table-connect/checkout/checkout.component').then(m => m.TableConnectCheckoutComponent),
    data: { header: { show: true, title: 'Checkout', backAriaLabel: 'Back to cart', backHref: ['cart'] } },
  },
  // TC-8 — public, no-auth itemised active-bill view. Read-only.
  {
    path: 't/:publicToken/bill',
    loadComponent: () => import('./features/table-connect/bill/bill.component').then(m => m.TableConnectBillComponent),
    data: { header: { show: true, title: 'Your Bill', backAriaLabel: 'Back to landing', backHref: [] } },
  },
  // TC-9 — payment result page. Polls /payment-intents/{id}/status.
  {
    path: 't/:publicToken/payment-result',
    loadComponent: () => import('./features/table-connect/payment-result/payment-result.component').then(m => m.TableConnectPaymentResultComponent),
    data: { header: { show: true, title: 'Payment' } },   // back set dynamically (cart vs bill)
  },
  // TC-10 — digital receipt + optional Google review CTA. Read-only. 30-day expiry.
  {
    path: 't/:publicToken/receipt/:paymentIntentId',
    loadComponent: () => import('./features/table-connect/receipt/receipt.component').then(m => m.TableConnectReceiptComponent),
    data: { header: { show: true, title: 'Receipt', backAriaLabel: 'Back to landing', backHref: [] } },
  },
  // TC-14C — pickup-order status (polling).
  {
    path: 't/:publicToken/pickup/:orderReference',
    loadComponent: () => import('./features/table-connect/pickup-status/pickup-status.component').then(m => m.TableConnectPickupStatusComponent),
    data: { header: { show: true, backAriaLabel: 'Back to landing', backHref: [] } },
  },
  // ─── TC-A2 — guard-free login, reachable from any screen on any store
  // (incl. pickup-only). Honours a sanitized ?returnUrl so sign-in lands back
  // where the user was. The delivery flow keeps its own /delivery/login. ───
  {
    path: 't/:publicToken/login',
    loadComponent: () => import('./features/table-connect/delivery/customer-auth/customer-auth.component').then(m => m.CustomerAuthComponent),
  },
  // ─── D4A — COD delivery guest flow (dark unless the store enables delivery) ───
  // deliveryEnabledGuard resolves fulfilment-config and redirects to the store
  // landing when delivery is off, so the feature never flashes for pickup-only
  // stores and existing token routes are untouched.
  {
    path: 't/:publicToken/delivery',
    canActivate: [deliveryEnabledGuard],
    loadComponent: () => import('./features/table-connect/delivery/delivery-checkout/delivery-checkout.component').then(m => m.DeliveryCheckoutComponent),
  },
  {
    path: 't/:publicToken/delivery/login',
    canActivate: [deliveryEnabledGuard],
    loadComponent: () => import('./features/table-connect/delivery/customer-auth/customer-auth.component').then(m => m.CustomerAuthComponent),
  },
  {
    path: 't/:publicToken/delivery/address',
    canActivate: [deliveryEnabledGuard],
    loadComponent: () => import('./features/table-connect/delivery/delivery-checkout/delivery-checkout.component').then(m => m.DeliveryCheckoutComponent),
  },
    {
    path: 't/:publicToken/delivery/confirmed/:orderReference',
    loadComponent: () => import('./features/table-connect/delivery/delivery-ack/delivery-ack.component').then(m => m.DeliveryAckComponent),
  },
  // ─── D9 — customer account area (auth-only; delivery must be enabled) ───
  {
    path: 't/:publicToken/account',
    canActivate: [customerAuthGuard],
    loadComponent: () => import('./features/table-connect/account/account-hub/account-hub.component').then(m => m.AccountHubComponent),
  },
  {
    path: 't/:publicToken/account/profile',
    canActivate: [customerAuthGuard],
    loadComponent: () => import('./features/table-connect/account/account-profile/account-profile.component').then(m => m.AccountProfileComponent),
  },
  {
    path: 't/:publicToken/account/addresses',
    canActivate: [customerAuthGuard],
    loadComponent: () => import('./features/table-connect/account/account-addresses/account-addresses.component').then(m => m.AccountAddressesComponent),
  },
  {
    path: 't/:publicToken/account/orders',
    canActivate: [customerAuthGuard],
    loadComponent: () => import('./features/table-connect/account/account-orders/account-orders.component').then(m => m.AccountOrdersComponent),
  },
  // pickup-display (counter TV board) and kiosk surfaces are operator-
  // facing, not guest-facing — they live in the primary src/web
  // workspace, not in this guest SPA. The TableConnect surface only
  // hosts the QR-scanned guest flows (t/:publicToken/...).
  { path: '**', component: NotFoundComponent },
];
